import {
  getAdCookies,
  getAMP,
  getAnalyticsIds,
  getInitialSourceTouch,
  getSessionSourceTouch,
  getSourceTouch,
} from "./ad_utm";
import type { IConfigStore } from "./configstore";
import type {
  AMP,
  CommonPropType,
  EventType,
  EventTypes,
  SourceTouch,
  UserTraits,
} from "./event_types";
import { ICP_PATH, type Event, type Session } from "./types";
import { type BatchUploader } from "./uploader";
import { deepEqual, uuid } from "./utils";

const ANONYMOUS_ID_KEY = "anonymous_id";
const USER_ID_KEY = "user_id";
const USER_TRAITS_KEY = "user_traits";
const ICPS_KEY = "icps";

export interface FetchIDOptions {
  timeout?: number;
  cacheOnly?: boolean;
  skipCache?: boolean;
}

export interface FetchIDResult {
  icps: string[] | null;
  error?: string;
}

/**
 * Options used when initializing the tracker.
 */
export interface InitOptions {
  sourceId?: string;
  uploader: BatchUploader;
  enableRealTimeID?: boolean;
  realTimeIDTimeout: number;
  onDisable?: () => void;
  /**
   * The source touch read from the landing URL when the SDK booted. Used
   * instead of the current URL, which may have lost its parameters by the
   * time tracking is enabled (e.g. after a consent banner or a router
   * stripping them).
   */
  landingTouch?: SourceTouch;
}

const PERSONAL_EMAIL_DOMAINS = [
  "gmail.com",
  "yahoo.com",
  "hotmail.com",
  "outlook.com",
  "aol.com",
  "icloud.com",
];
export default class AutoTracker<E extends EventTypes> {
  options: InitOptions;

  configStore: IConfigStore;

  anonymousId: string;
  userId: string | undefined;
  userTraits: UserTraits | undefined;
  icps: string[] | null = null;
  session: Session | undefined;
  amp: AMP | undefined;

  initialSource: SourceTouch | undefined;
  source: SourceTouch | undefined;

  uploader: BatchUploader;
  private disabled = false;
  private lastSearch: string;
  private readonly fetchAbortControllers = new Set<AbortController>();

  constructor(options: InitOptions, store: IConfigStore) {
    this.options = options;
    this.configStore = store;
    this.uploader = options.uploader;
    this.anonymousId = this.configStore.get(ANONYMOUS_ID_KEY) as string;
    if (this.anonymousId == null) {
      // generate a new anonymous id
      this.anonymousId = uuid();
    }
    // refresh expiration date.
    this.configStore.set(ANONYMOUS_ID_KEY, this.anonymousId);

    this.userId = this.configStore.get(USER_ID_KEY) as string;
    this.userTraits =
      (this.configStore.get(USER_TRAITS_KEY) as Record<
        string,
        CommonPropType
      >) ?? {};

    this.icps = this.configStore.get(ICPS_KEY) as string[];
    const landingTouch = options.landingTouch ?? getSourceTouch();
    this.source = getSessionSourceTouch(this.configStore, landingTouch);
    this.initialSource = getInitialSourceTouch(this.configStore, landingTouch);
    this.lastSearch = location.search;
  }

  /**
   * Returns true if the user was updated. false if it is duplicate call.
   * @param userId
   * @param traits
   * @returns
   */
  __setUser(userId: string, traits: UserTraits): boolean {
    let newTraits = traits;
    if (this.userId === userId) {
      newTraits = {
        ...this.userTraits,
        ...traits,
      };
      if (deepEqual(this.userTraits, newTraits)) {
        return false;
      }
    }

    this.userId = userId;
    this.userTraits = newTraits;
    this.icps = [];
    this.configStore.set(USER_ID_KEY, userId);
    this.configStore.set(USER_TRAITS_KEY, newTraits);
    this.configStore.set(ICPS_KEY, []);
    return true;
  }

  enable(): void {
    this.disabled = false;
    window.syftc.enabled = true;
  }

  disable(): void {
    if (this.options.onDisable) {
      this.options.onDisable();
      return;
    }
    this.dispose();
  }

  dispose(): void {
    this.disabled = true;
    this.fetchAbortControllers.forEach((controller) => controller.abort());
    this.fetchAbortControllers.clear();
    window.syftc.enabled = false;
  }

  /**
   * Refreshes/fetches the ICPs if required.
   * @param eventType The type of event to refresh the ICPs for.
   * @param force Whether to force a refresh of the ICPs.
   * @param timeout The timeout for the fetch.
   * @returns The ICPs for the given event type.
   */
  async __refreshICPs(
    eventType: string,
    force: boolean = false,
    timeout?: number
  ): Promise<FetchIDResult> {
    if (this.disabled) {
      return { icps: null, error: "Tracking is disabled" };
    }
    if (!this.options.sourceId) {
      return {
        error: "No sourceId configured",
        icps: null,
      };
    }
    if (!this.options.enableRealTimeID) {
      return {
        error: "enableRealTimeID is not enabled",
        icps: null,
      };
    }

    if (!force && this.icps != null) {
      return {
        icps: this.icps,
      };
    }

    this.icps = null;
    this.configStore.remove(ICPS_KEY);
    const actualTimeout = timeout ?? this.options.realTimeIDTimeout;
    const controller = new AbortController();
    this.fetchAbortControllers.add(controller);
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    try {
      const partialEvent = this._getPartialEvent();
      const event: Event = {
        ...partialEvent,
        type: eventType as EventType,
        properties: {},
      };
      const respF = fetch(ICP_PATH, {
        priority: "high",
        method: "POST",
        signal: controller.signal,
        body: JSON.stringify({
          sourceId: this.options.sourceId,
          identity: event,
        }),
      }).then(async (resp) => {
        if (!resp.ok) {
          throw new Error("Failed to fetch ICPs");
        }
        const data = await resp.json();
        if (this.disabled) {
          throw new Error("Tracking is disabled");
        }
        this.icps = data.icps ?? [];
        this.configStore.setWithExpiration(ICPS_KEY, this.icps, 7);
        return this.icps as string[];
      });

      // The timeout below only stops the caller waiting; the request is left
      // running so a late response still warms the ICP cache. It is aborted
      // only by dispose(), which is why the controller stays registered until
      // the request itself settles.
      respF
        .catch(() => {})
        .finally(() => {
          this.fetchAbortControllers.delete(controller);
        });

      const resp = await Promise.race([
        respF,
        new Promise<never>((_, reject) => {
          timeoutId = setTimeout(
            () => reject(new Error("Timeout")),
            Math.max(2000, actualTimeout) // temporary hack for heygen to work.
          );
        }),
      ]);
      return {
        icps: resp,
      };
    } catch (error) {
      // Silently fail on timeout or other errors
      return {
        error: this.disabled ? "Tracking is disabled" : "Failed to fetch ICPs",
        icps: null,
      };
    } finally {
      clearTimeout(timeoutId);
    }
  }

  isICPDisabledForEmail(email: string): boolean {
    return PERSONAL_EMAIL_DOMAINS.some((domain) => email.includes(domain));
  }

  async identify(userId: string, traits: UserTraits = {}): Promise<string[]> {
    if (this.disabled) return [];
    if (!this.__setUser(userId, traits)) {
      if (this.isICPDisabledForEmail(userId)) {
        return [];
      }
      return this.icps ?? [];
    }
    const partialEvent = this._getPartialEvent();
    const event: Event = {
      ...partialEvent,
      type: "identify",
      context: {
        ...partialEvent.context,
        traits: undefined,
      },
      traits: this.userTraits,
    };
    this.uploader.addToQueue(event);
    if (this.isICPDisabledForEmail(userId)) {
      return [];
    }
    const data = await this.__refreshICPs(event.type, true);
    return data.icps ?? [];
  }

  track<N extends keyof E>(name: N, properties: E[N]): void {
    if (this.disabled) return;
    const partialEvent = this._getPartialEvent();
    this.uploader.addToQueue({
      ...partialEvent,
      event: name as string,
      name: name as string,
      type: "track",
      properties: {
        ...partialEvent.properties,
        ...properties,
      },
    });
  }

  async signup(email: string, traits: UserTraits = {}): Promise<string[]> {
    if (this.disabled) return [];
    if (!this.__setUser(email, traits)) {
      if (this.isICPDisabledForEmail(email)) {
        return [];
      }
      return this.icps ?? [];
    }
    const partialEvent = this._getPartialEvent();
    const event: Event = {
      ...partialEvent,
      type: "signup",
      context: {
        ...partialEvent.context,
        traits: undefined,
      },
      traits: this.userTraits,
    };
    this.uploader.addToQueue(event);
    if (this.isICPDisabledForEmail(email)) {
      return [];
    }
    const data = await this.__refreshICPs(event.type, true);
    return data.icps ?? [];
  }

  page(category?: string, page?: string, props?: E["page"]): void {
    if (this.disabled) return;
    const partialEvent = this._getPartialEvent();
    this.uploader.addToQueue({
      ...partialEvent,
      type: "page",
      name: page,
      properties: {
        ...partialEvent.properties,
        ...props,
        category,
      },
    });
  }

  /**
   * Picks up a new campaign or click ID after a client-side navigation, which
   * does not re-run the constructor. Checked per event rather than per page
   * view, since page views are deduplicated by path and can be turned off.
   */
  private refreshSource(): void {
    if (location.search === this.lastSearch) return;
    this.lastSearch = location.search;
    this.source = getSessionSourceTouch(this.configStore);
  }

  async fetchID(options: FetchIDOptions = {}): Promise<FetchIDResult> {
    if (this.disabled) {
      return { icps: null, error: "Tracking is disabled" };
    }
    try {
      // Handle cacheOnly option - return immediately from cache
      if (options.cacheOnly) {
        const cachedICPs = this.icps?.length ? this.icps : null;
        return { icps: cachedICPs };
      }
      return await this.__refreshICPs(
        "page",
        options.skipCache,
        options.timeout
      );
    } catch (error) {
      return {
        icps: null,
        error: error instanceof Error ? error.message : "Failed to fetch ICPs",
      };
    }
  }

  _getPartialEvent(): Omit<Event, "event" | "type"> {
    this.refreshSource();
    return {
      messageId: uuid(),
      userId: this.userId,
      anonymousId: this.anonymousId,
      properties: {},
      timestamp: new Date(),
      session: this.session
        ? {
            id: this.session.id,
            startTime: this.session.startTime,
          }
        : undefined,
      context: {
        traits: this.userTraits,
        locale: navigator.language,
        page: {
          path: location.pathname,
          referrer: document.referrer ?? null,
          search: location.search,
          title: document.title,
          url: location.href,
        },

        ...this.source,
        initialSource: this.initialSource,

        amp: getAMP(),
        analyticsIds: getAnalyticsIds(),
        adCookies: getAdCookies(),
        deviceWidth: window.innerWidth,
      },
    };
  }
}
